import type { TextFormat } from "./content";
import type { Store } from "./storage";

// What someone was writing in each chat, kept on this device so it survives closing the app. A
// draft lives in the same per-account store as the message cache, so it never leaves the device
// and goes when the account signs out here.

export type Draft = {
  body: string;
  format?: TextFormat;
  /** User ids of the people mentioned in the body. */
  mentions?: string[];
  /** The message being replied to. */
  replyTo?: string;
  /** When it last changed, in milliseconds since 1970. */
  updatedAt: number;
};

export type DraftInput = Omit<Draft, "updatedAt">;

const PREFIX = "draft:";
/** Typing changes a draft many times a second. Writes to the device wait this long for a pause. */
const WRITE_DELAY = 400;

function isBlank(d: DraftInput): boolean {
  return !d.body.trim() && !d.replyTo;
}

/**
 * Drafts per chat, or per thread with a key from threadKey(). Reads come from memory once load()
 * has run. Writes reach memory at once and the device shortly after; flush() writes at once, for
 * when the page is closing.
 */
export class DraftStore {
  private drafts = new Map<string, Draft>();
  private pending = new Map<string, ReturnType<typeof setTimeout>>();
  private listeners = new Set<() => void>();
  private loading: Promise<void> | null = null;
  private loaded = false;

  constructor(
    private store: Store,
    private now: () => number = Date.now,
  ) {}

  /** Reads every draft on the device. Safe to call more than once. */
  load(): Promise<void> {
    this.loading ??= (async () => {
      const keys = await this.store.keys(PREFIX);
      const found = await Promise.all(keys.map(async (k) => [k, await this.store.get<Draft>(k)] as const));
      for (const [k, d] of found) {
        const key = k.slice(PREFIX.length);
        // A draft typed before loading finished is newer than the stored one.
        if (d && typeof d.body === "string" && !this.drafts.has(key)) this.drafts.set(key, d);
      }
      this.loaded = true;
      this.emit();
    })();
    return this.loading;
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  get(key: string): Draft | undefined {
    return this.drafts.get(key);
  }

  /** Every chat or thread with a draft, newest first, for "Draft" labels in the chat list. */
  list(): { key: string; draft: Draft }[] {
    return [...this.drafts]
      .map(([key, draft]) => ({ key, draft }))
      .sort((a, b) => b.draft.updatedAt - a.draft.updatedAt);
  }

  has(key: string): boolean {
    return this.drafts.has(key);
  }

  /** Keeps a draft. An empty one, with no reply either, clears it. */
  set(key: string, input: DraftInput): void {
    if (isBlank(input)) {
      this.clear(key);
      return;
    }
    const current = this.drafts.get(key);
    if (
      current &&
      current.body === input.body &&
      current.format === input.format &&
      current.replyTo === input.replyTo &&
      (current.mentions ?? []).join() === (input.mentions ?? []).join()
    )
      return;
    const draft: Draft = {
      body: input.body,
      ...(input.format ? { format: input.format } : {}),
      ...(input.mentions?.length ? { mentions: [...input.mentions] } : {}),
      ...(input.replyTo ? { replyTo: input.replyTo } : {}),
      updatedAt: this.now(),
    };
    this.drafts.set(key, draft);
    this.schedule(key);
    this.emit();
  }

  /** Drops a draft, as when its message is sent. */
  clear(key: string): void {
    if (!this.drafts.has(key)) return;
    this.drafts.delete(key);
    this.schedule(key);
    this.emit();
  }

  /** Writes every waiting change to the device now. */
  async flush(): Promise<void> {
    const keys = [...this.pending.keys()];
    for (const key of keys) clearTimeout(this.pending.get(key));
    this.pending.clear();
    await Promise.all(keys.map((key) => this.write(key)));
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private schedule(key: string) {
    clearTimeout(this.pending.get(key));
    this.pending.set(
      key,
      setTimeout(() => {
        this.pending.delete(key);
        void this.write(key);
      }, WRITE_DELAY),
    );
  }

  private async write(key: string) {
    const draft = this.drafts.get(key);
    try {
      if (draft) await this.store.set(PREFIX + key, draft);
      else await this.store.delete(PREFIX + key);
    } catch {
      // The draft stays in memory for this session. Losing it on restart beats breaking typing.
    }
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }
}
